
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * WorkingTimePeriod struct
 */
class WorkingTimePeriod {
  /**
   * Initialize struct
   * @param {!number=} startTime
   * @param {!number=} endTime
   * @constructor
   */
  constructor (startTime = new UInt64(0, 0), endTime = new UInt64(0, 0)) {
    this.StartTime = startTime
    this.EndTime = endTime
  }

  /**
   * Copy struct (shallow copy)
   * @this {!WorkingTimePeriod}
   * @param {!WorkingTimePeriod} other Other struct
   * @returns {!WorkingTimePeriod} This struct
   */
  copy (other) {
    if (other.StartTime != null) {
      this.StartTime = UInt64.fromNumber(other.StartTime)
    } else {
      this.StartTime = null
    }
    if (other.EndTime != null) {
      this.EndTime = UInt64.fromNumber(other.EndTime)
    } else {
      this.EndTime = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!WorkingTimePeriod}
   * @returns {!WorkingTimePeriod} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new WorkingTimePeriodModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new WorkingTimePeriodModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!WorkingTimePeriod}
   * @param {!WorkingTimePeriod} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof WorkingTimePeriod)) {
      throw new TypeError('Instance of WorkingTimePeriod is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!WorkingTimePeriod}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      StartTime: ((this.StartTime != null) ? this.StartTime.toNumber() : null),
      EndTime: ((this.EndTime != null) ? this.EndTime.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return WorkingTimePeriod.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!WorkingTimePeriod} other Object value
   * @returns {!WorkingTimePeriod} Created struct
   */
  static fromObject (other) {
    return new WorkingTimePeriod().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!WorkingTimePeriod}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return WorkingTimePeriod.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!WorkingTimePeriod}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 61
  }
}

export { WorkingTimePeriod };

/**
 * Fast Binary Encoding WorkingTimePeriod field model
 */
class FieldModelWorkingTimePeriod extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.StartTime = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.EndTime = new fbe.FieldModelUInt64(buffer, this.StartTime.fbeOffset + this.StartTime.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelWorkingTimePeriod}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelWorkingTimePeriod}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.StartTime.fbeSize + this.EndTime.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelWorkingTimePeriod}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.StartTime.fbeExtra + this.EndTime.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelWorkingTimePeriod}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelWorkingTimePeriod.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelWorkingTimePeriod}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 61
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelWorkingTimePeriod}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelWorkingTimePeriod}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.StartTime.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.StartTime.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.StartTime.fbeSize

    if ((fbeCurrentSize + this.EndTime.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.EndTime.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EndTime.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelWorkingTimePeriod}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelWorkingTimePeriod}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelWorkingTimePeriod}
   * @param {!WorkingTimePeriod} fbeValue Default value, defaults is new WorkingTimePeriod()
   * @returns {!WorkingTimePeriod} WorkingTimePeriod value
   */
  get (fbeValue = new WorkingTimePeriod()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelWorkingTimePeriod}
   * @param {!WorkingTimePeriod} fbeValue WorkingTimePeriod value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.StartTime.fbeSize) <= fbeStructSize) {
      fbeValue.StartTime = this.StartTime.get()
    } else {
      fbeValue.StartTime = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.StartTime.fbeSize

    if ((fbeCurrentSize + this.EndTime.fbeSize) <= fbeStructSize) {
      fbeValue.EndTime = this.EndTime.get()
    } else {
      fbeValue.EndTime = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EndTime.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelWorkingTimePeriod}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelWorkingTimePeriod}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelWorkingTimePeriod}
   * @param {!WorkingTimePeriod} fbeValue WorkingTimePeriod value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelWorkingTimePeriod}
   * @param {WorkingTimePeriod} fbeValue WorkingTimePeriod value
   */
  setFields (fbeValue) {
    this.StartTime.set(fbeValue.StartTime)
    this.EndTime.set(fbeValue.EndTime)
  }
}

export { FieldModelWorkingTimePeriod };

/**
 * Fast Binary Encoding WorkingTimePeriod model
 */
class WorkingTimePeriodModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelWorkingTimePeriod(this.buffer, 4)
  }

  /**
   * Get the WorkingTimePeriod model
   * @this {!WorkingTimePeriodModel}
   * @returns {!FieldModelWorkingTimePeriod} model WorkingTimePeriod model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!WorkingTimePeriodModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!WorkingTimePeriodModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return WorkingTimePeriodModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!WorkingTimePeriodModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelWorkingTimePeriod.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!WorkingTimePeriodModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!WorkingTimePeriodModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!WorkingTimePeriodModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!WorkingTimePeriodModel}
   * @param {!WorkingTimePeriod} value WorkingTimePeriod value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!WorkingTimePeriodModel}
   * @param {!WorkingTimePeriod} value WorkingTimePeriod value, defaults is new WorkingTimePeriod()
   * @return {!object} Deserialized WorkingTimePeriod value and its size
   */
  deserialize (value = new WorkingTimePeriod()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new WorkingTimePeriod(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new WorkingTimePeriod(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!WorkingTimePeriodModel}
   * @param {!number} prev Previous WorkingTimePeriod model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { WorkingTimePeriodModel };
